import {
  activeChatbotIdSelector,
  activeFacebookPageIdSelector,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  IntegrationRevenue,
  RevenueType,
} from '../IntegrationRevenue/IntegrationRevenue';
import { useConnectKlaviyo } from './use-connect-klaviyo';
import styles from './KlaviyoIntegrations.module.css';
import PrimarySelect, {
  DropdownOption,
} from '@mm/components/legacy-components/PrimarySelect/PrimarySelect';
import { Radio } from 'antd';
import classNames from 'classnames';
import { KlaviyoIcon } from '@mm/icons';
import { Button, Link } from '@mm/components';
import { BotBuilderRoutes } from '../../../../../../../../routes/bot-builder-routes';
import { useHistory } from 'react-router';
import { FileOutlined } from '@ant-design/icons';
import { useWhitelabelConfig } from '@mm/hooks/src/use-whitelabel-config';

export const KlaviyoRevenue = ({
  dashboardLayout,
  css,
}: {
  dashboardLayout?: boolean;
  css?: {
    revenueBox?: string;
    revenueBoxesWrapper?: string;
    revenueData?: string;
  };
}) => {
  const activeChatbotId = useSelector(activeChatbotIdSelector);
  const activeFbPageId = useSelector(activeFacebookPageIdSelector);
  const revenue = useSelector((state: StoreState) => state.klaviyo.revenue);
  const { getRevenues } = useConnectKlaviyo();
  const history = useHistory();

  const { companyName = 'Customers.ai' } = useWhitelabelConfig();

  const helpLink =
    'https://customers.ai/help/l/en/article/2n5qywqrod-creating-a-custom-report-in-klaviyo-for-customers-ai';

  const options = [
    { id: 'revenue', name: 'Post email send revenue' },
    {
      id: 'purchase',
      name: `${companyName} contact purchase value & purchases`,
    },
  ] as DropdownOption[];

  const [activeOption, setActiveOption] = useState(options[0]);

  const onRevenueClick = (type: RevenueType) => {
    switch (type) {
      case 'purchase_amount':
      case 'purchase_value':
        history.push({
          pathname: BotBuilderRoutes.contacts.getLink({
            chatbotId: activeChatbotId,
          }),
          state: { query: { attributes: { klaviyo_purchased: [true] } } },
        });
        break;

      case 'revenue_post_email':
        history.push({
          pathname: BotBuilderRoutes.contacts.getLink({
            chatbotId: activeChatbotId,
          }),
          state: {
            query: {
              attributes: { klaviyo_post_email_revenue: [{ gt: '0' }] },
            },
          },
        });
        break;
      default:
        break;
    }
  };

  const customOptionRenderer = (option: DropdownOption) => (
    <div>
      <Radio
        defaultChecked={activeOption.id === option.id}
        className={styles.radioInput}
      >
        {option.name}
      </Radio>
    </div>
  );

  const selector = (
    <PrimarySelect
      options={options}
      value={activeOption}
      onChange={setActiveOption}
      customOptionRenderer={customOptionRenderer}
      showActiveItem={false}
      className={styles.revenueOptionSelect}
      activeItemClassName={styles.revenueActiveItem}
      useAntIcons
    />
  );

  return (
    <div className={styles.revenueContainer}>
      {dashboardLayout && (
        <div className={styles.revenueHeaderDashboardLayout}>
          <div>
            {dashboardLayout && (
              <div className={styles.klaviyoLogo}>
                <KlaviyoIcon size={18} color="#262626" />
                Klaviyo
              </div>
            )}

            <div
              className={classNames(styles.heading, {
                [styles.klaviyoDashboardTitle]: dashboardLayout,
              })}
            >
              Revenue generated from your leads
            </div>
          </div>
          {selector}
          <Button
            color="secondary-blue"
            css={{
              container: classNames(styles.secondaryButton, styles.leftAligned),
            }}
            onClick={() => {
              history.push(
                BotBuilderRoutes.integrations.getLink({
                  chatbotId: activeChatbotId,
                }),
                {
                  kind: 'native-integrations',
                },
              );
            }}
          >
            Go to Integration
          </Button>
        </div>
      )}

      {!dashboardLayout && (
        <div className={classNames(styles.heading, styles.revenueHeading)}>
          Revenue generated from your leads
          {selector}
          <Link className={styles.link} href={helpLink} isBlank>
            {' '}
            <FileOutlined className={styles.linkIcon} />
            How to create a Klaviyo ROI Report
          </Link>
        </div>
      )}

      <IntegrationRevenue
        platformName="Klaviyo"
        tooltipTitle="Go to Klaviyo Audience"
        className={styles.revenue}
        totalRevenue={revenue.totalRevenue}
        totalOrders={revenue.totalOrders}
        totalRevenuePostCreate={revenue.totalRevenuePostCreate}
        totalRevenuePostEmail={revenue.totalRevenuePostEmail}
        getRevenues={getRevenues}
        facebookPageId={activeFbPageId}
        base={activeOption.id === 'purchase'}
        advanced={activeOption.id === 'revenue'}
        css={{ revenueBox: styles.revenueBox, ...css }}
        onClick={onRevenueClick}
      />
    </div>
  );
};
